import {
	ButtonWithSpinner,
	FormFieldControl,
	Input,
	Select,
	ToastCustom,
} from "@components";
import { useMutation } from "@tanstack/react-query";
import { maskOdometer } from "@utils";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { FuelLevelSlider } from "@/components/base/slider/fuel-level-slider";
import { CHECKLIST_TYPES, UI_TEXT_GENERAL_INFOS } from "../constants";
import { generalInfosResolver } from "../schema";
import { checklistAnswerGeneral } from "../services";
import { useChecklistProgressStore, useChecklistStore } from "../store";
import type { IGeneralInfosForm } from "../types";
import { mergeChecklistAnswerResponse } from "../utils/checklist-answer.utils";
import { StepHeader } from "./";

export const GeneralInfosStep = () => {
	const [isOdometerFocused, setIsOdometerFocused] = useState(false);

	const checklist = useChecklistStore((state) => state.checklist);
	const { answerData } = checklist || {};
	const nextStep = useChecklistProgressStore((state) => state.nextStep);
	const checklistAnswer = answerData?.checklistAnswer;
	const setChecklist = useChecklistStore((state) => state.setChecklist);

	const defaultChecklistType =
		CHECKLIST_TYPES.find(
			(tp) => tp.description === checklistAnswer?.checklistType
		)?.id?.toString() ?? "";

	const { mutate, isPending } = useMutation({
		mutationKey: ["checklist:answerGeneral"],
		mutationFn: checklistAnswerGeneral,
		onSuccess: (data) => {
			if (!checklist?.answerData) {
				return;
			}

			setChecklist({
				linkData: checklist.linkData,
				answerData: mergeChecklistAnswerResponse(checklist.answerData, data),
			});

			nextStep();
		},
		onError: (error) => {
			if (error?.message) {
				toast(<ToastCustom description={error?.message} title="Erro" />);
			}
		},
	});

	// Função para arredondar o nível de combustível para os steps permitidos (0, 25, 50, 75, 100)
	const roundFuelLevel = (value: number): number => {
		const steps = [0, 25, 50, 75, 100];
		return steps.reduce((prev, curr) =>
			Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
		);
	};

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<IGeneralInfosForm>({
		resolver: generalInfosResolver,
		defaultValues: {
			odometer: checklistAnswer?.odometer?.toString() ?? "",
			checklistType: defaultChecklistType,
			fuelLevel: roundFuelLevel(checklistAnswer?.fuelLevel ?? 0),
		},
		context: {
			isTrailerOrSemiTrailer: answerData?.isTrailerOrSemiTrailer,
		},
		mode: "onChange",
		disabled: isPending,
	});

	const onSubmit = (data: IGeneralInfosForm) => {
		if (answerData?.checklistAnswer) {
			const odometerDigits = data.odometer.replace(/\D/g, "");

			mutate({
				answerToken: answerData.checklistAnswer.answerToken,
				checklistType: Number(data.checklistType),
				odometer: odometerDigits ? Number(odometerDigits) : null,
				fuelLevel: data.fuelLevel,
				answerId: answerData.checklistAnswer.id,
			});
		}
	};

	const getOdometerDisplayValue = (value: string): string => {
		const formattedValue = maskOdometer(value);
		const odometerWithKmSuffix = formattedValue ? `${formattedValue} km` : "";
		return isOdometerFocused ? formattedValue : odometerWithKmSuffix;
	};

	return (
		<form
			className="mx-auto flex min-h-dvh w-full flex-col gap-5 bg-secondary pt-5"
			onSubmit={handleSubmit(onSubmit)}
		>
			<StepHeader
				subtitle="Preencher seus dados e responder as perguntas"
				title="Dados gerais"
			/>

			<div className="mx-auto flex h-full w-full flex-col items-center gap-4 px-4">
				<Controller
					control={control}
					name="odometer"
					render={({ field: { value, onChange, disabled } }) => {
						const displayValue = getOdometerDisplayValue(value);

						return (
							<FormFieldControl
								className="max-w-[456px]"
								error={errors.odometer?.message}
								htmlFor="odometer"
								isRequired={!answerData?.isTrailerOrSemiTrailer}
								label={UI_TEXT_GENERAL_INFOS.odometerLabel}
							>
								<Input
									aria-label="odometer"
									disabled={disabled}
									error={!!errors.odometer?.message}
									inputMode="numeric"
									onBlur={() => {
										setIsOdometerFocused(false);
									}}
									onChange={({ target }) => {
										const newValue = target.value;
										// Remove tudo que não for dígito (pontos serão adicionados pela máscara)
										// Remove também " km" caso o usuário tenha digitado
										const onlyNumbers = newValue.replace(/\D/g, "");

										// Limita a 6 caracteres
										if (onlyNumbers.length > 6) {
											return;
										}

										onChange(onlyNumbers);
									}}
									onFocus={() => {
										setIsOdometerFocused(true);
									}}
									placeholder={UI_TEXT_GENERAL_INFOS.odometerPlaceholder}
									type="text"
									value={displayValue}
								/>
							</FormFieldControl>
						);
					}}
				/>

				<Controller
					control={control}
					name="checklistType"
					render={({ field: { value, onChange, disabled } }) => (
						<FormFieldControl
							className="max-w-[456px]"
							error={errors.checklistType?.message}
							htmlFor="checklistType"
							isRequired
							label={UI_TEXT_GENERAL_INFOS.checklistTypeLabel}
						>
							<Select
								aria-label={UI_TEXT_GENERAL_INFOS.checklistTypeLabel}
								className="w-full"
								isDisabled={disabled}
								items={CHECKLIST_TYPES}
								onChange={onChange}
								placeholder={UI_TEXT_GENERAL_INFOS.checklistTypePlaceholder}
								value={value}
							>
								{(item) => (
									<Select.Item id={item.id} isDisabled={disabled}>
										{item.label}
									</Select.Item>
								)}
							</Select>
						</FormFieldControl>
					)}
					rules={{ required: true }}
				/>

				<Controller
					control={control}
					name="fuelLevel"
					render={({ field: { value, onChange, disabled } }) => (
						<FormFieldControl
							className="max-w-[456px] px-1"
							error={errors.fuelLevel?.message}
							htmlFor="fuelLevel"
							isRequired={!answerData?.isTrailerOrSemiTrailer}
							label={UI_TEXT_GENERAL_INFOS.fuelLevelLabel}
							showIconError={false}
						>
							<FuelLevelSlider
								className="w-full"
								isDisabled={disabled}
								onChange={onChange}
								value={value}
							/>
						</FormFieldControl>
					)}
				/>
			</div>

			<div className="sticky bottom-0 left-0 mt-auto w-full border-primary border-t bg-secondary px-4 pt-4 pb-8 text-center">
				<ButtonWithSpinner
					className="max-w-[456px]"
					isLoading={isPending}
					size="lg"
				>
					{UI_TEXT_GENERAL_INFOS.submitButton}
				</ButtonWithSpinner>
			</div>
		</form>
	);
};
